import { React, useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { fieldStyles } from "services/utils";
import ConfirmationDialog from "components/ConfirmationDialog";

const RevenueGeneratorFields = ({
  id,
  formData,
  productivityInputs,
  productivityOptions,
  handleChangeRev,
  handleProductivityChange,
  handleSave,
  handleUpdate,
  handleDelete,
  revLoading,
  revUpdateLoading,
  t,
}) => {
  const { raw = "", period = "month" } = productivityInputs || {};
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const confirmClose = () => setConfirmOpen(false);

  return (
    <Box mt={2} display="flex" flexDirection="column" gap={2}>
      {/* Company Targets Per Year */}

      <Box display="flex" gap={2} flexWrap="wrap">
        <Box>
          <TextField
            label={t("admin.manpowerRequirement.name")}
            type="text"
            margin="dense"
            size="small"
            value={formData?.name || ""} // 👈 use formData here
            onChange={(e) => handleChangeRev(id, "name", e.target.value)}
            sx={{ width: { xs: "100%", sm: 300 } }}
          />

          <Typography
            variant="caption"
            color="error" // or "textSecondary"
            sx={{ display: "block", ml: 0.5, ml: 1, mt: 0.5, fontSize: 12 }}
          >
            {/* Include Revenue in name if it is financial. */}
            {t("user.manpowerPlanningAnalysis.chart.opexCalc")}
          </Typography>
        </Box>

        <TextField
          label={t("admin.manpowerRequirement.companyTargetsPerYear")}
          type="number"
          margin="dense"
          size="small"
          value={formData?.companyTargetsPerYear || ""}
          onChange={(e) => {
            const value = e.target.value;
            if (value >= 0 || value === "") {
              handleChangeRev(id, "companyTargetsPerYear", value); // ✅ use id
            }
          }}
          inputProps={{ min: 0, step: 1 }}
          sx={{ width: { xs: "100%", sm: 200 } }}
        />
      </Box>

      {/* Employee Productivity + Period */}
      <Box display="flex" gap={2} flexWrap="wrap">
        <TextField
          label={t("admin.manpowerRequirement.employeeAvgAchievements")}
          type="number"
          margin="dense"
          size="small"
          // value={formData?.avgProductivityPerMonth || ""}
          value={raw}
          onChange={(e) => {
            // const value = e.target.value === "" ? "" : Number(e.target.value);
            const value = e.target.value;
            if (value >= 0 || value === "") {
              handleProductivityChange(id, value, period); // ✅ use UI state
            }
          }}
          inputProps={{ min: 0, step: 1 }}
          // onChange={(e) => {
          //   const value = Number(e.target.value);
          //   if (value >= 0 || e.target.value === "") {
          //     handleProductivityChange(id, value, formData?.productivityPeriod);  // ✅ update raw + recalc
          //   }
          // }}
          sx={{ width: { xs: "100%", sm: 300 } }}
        />

        <FormControl margin="dense" size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t("admin.manpowerRequirement.period")}</InputLabel>
          <Select
            // value={"month"}
            value={period}
            onChange={(e) => handleProductivityChange(id, raw, e.target.value)}
            // onChange={(e) => handleProductivityChange(productivityRaw, e.target.value)}
            sx={fieldStyles}
          >
            {productivityOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {formData.isNew ? (
          // Show Save button for new rows
          revLoading ? (
            <Box display="flex" justifyContent="flex-end" p={1}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Button
              color="primary"
              size="small"
              onClick={() => handleSave(formData)}
              sx={{
                maxHeight: "25px",
                fontSize: "0.7rem",
                mt: 1,
                "&:hover": { backgroundColor: "blue", color: "#fff" },
              }}
            >
              {t("admin.manpowerRequirement.save")}
            </Button>
          )
        ) : (
          // Show Update + Delete for existing rows'
          <Box display="flex" gap={2} mt={1}>
            {revUpdateLoading ? (
              <Box display="flex" alignItems="center" justifyContent="center" p={1}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Button
                color="primary"
                size="small"
                onClick={() => handleUpdate(formData)}
                sx={{
                  maxHeight: "25px",
                  fontSize: "0.7rem",
                  "&:hover": { backgroundColor: "blue", color: "#fff" },
                }}
              >
                {t("admin.manpowerRequirement.update")}
              </Button>
            )}

            <Button
              color="error"
              size="small"
              onClick={() => {
                setPendingDeleteId(formData.id);
                setConfirmOpen(true);
              }}
              sx={{
                color: "red",
                "&:hover": { backgroundColor: "red", color: "#fff" },
              }}
            >
              {t("admin.manpowerRequirement.delete")}
            </Button>
          </Box>
        )}

        <ConfirmationDialog
          open={confirmOpen}
          onClose={confirmClose}
          onConfirm={async () => {
            if (pendingDeleteId) {
              await handleDelete(pendingDeleteId);
              setPendingDeleteId(null);
            }
            confirmClose();
          }}
        />
      </Box>
    </Box>
  );
};

RevenueGeneratorFields.propTypes = {
  id: PropTypes.number.isRequired,
  formData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    jobId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    companyTargetsPerYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    avgProductivityPerMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    productivityPeriod: PropTypes.string,
    isNew: PropTypes.bool,
  }).isRequired,
  // productivityRaw: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  productivityInputs: PropTypes.shape({
    raw: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    period: PropTypes.string,
  }),
  productivityOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  handleChangeRev: PropTypes.func.isRequired,
  handleProductivityChange: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleUpdate: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  revLoading: PropTypes.bool.isRequired,
  revUpdateLoading: PropTypes.bool.isRequired,
};

export default RevenueGeneratorFields;
